import type { Ref } from 'react';
import { ThreadContextPanel } from './context-panel/ThreadContextPanel';
import { ConversationPane } from './panes/ConversationPane';
import { EmptyConversation } from './panes/EmptyConversation';
import { ThreadContextHost } from './panes/ThreadContextHost';
import { threadDisplayName } from './threadDisplayName';
import { useConversationActions } from './useConversationActions';
import type { useInboxThreads } from './useInboxThreads';
import { useLocalConversationResolution } from './useLocalConversationResolution';

/** Unterhaltung und — als dritte Spalte oder Blatt — ihr Kontext. */
export function InboxConversation({
  inbox,
  className,
  layerRef,
  contextAsColumn,
  contextOpen,
  onContextOpen,
  onBack,
}: {
  inbox: ReturnType<typeof useInboxThreads>;
  className: string;
  layerRef?: Ref<HTMLElement>;
  contextAsColumn: boolean;
  contextOpen: boolean;
  onContextOpen: (open: boolean) => void;
  onBack?: () => void;
}) {
  const { thread, demo } = inbox;
  const actions = useConversationActions(inbox.selectedId);
  const resolution = useLocalConversationResolution(inbox.selectedId);
  return (
    <>
      <section
        ref={layerRef}
        tabIndex={layerRef ? -1 : undefined}
        aria-label="Unterhaltung"
        className={className}
      >
        {thread ? (
          <ConversationPane
            thread={thread}
            messages={inbox.messages}
            messagesStatus={demo ? undefined : inbox.messagesState}
            onSend={
              demo
                ? () => Promise.reject(new Error('Vorschau'))
                : (text) => actions.reply.mutateAsync(text)
            }
            readOnly={demo}
            demoContext={inbox.demoCtx}
            onClose={demo ? undefined : () => actions.close.mutate()}
            onNoReplyNeeded={demo ? undefined : () => actions.noReply.mutate()}
            onMarkRead={inbox.markRead}
            onLocalResolution={demo ? undefined : () => resolution.run(thread)}
            resolving={resolution.pending}
            onBack={onBack}
            onOpenContext={contextAsColumn ? undefined : () => onContextOpen(true)}
          />
        ) : (
          <EmptyConversation
            loading={inbox.detailLoading}
            paused={inbox.detailPaused}
            error={inbox.detailError}
            onRetry={inbox.retryDetail}
            onBack={onBack ?? null}
          />
        )}
      </section>
      {thread && (
        <ThreadContextHost
          column={contextAsColumn}
          open={contextOpen}
          onClose={() => onContextOpen(false)}
          title={`Kontext · ${threadDisplayName(thread.guestName, thread.subject) ?? 'Gast'}`}
        >
          {/* key: beim Wechsel neu aufbauen, damit Notiz und Abruf nicht ins nächste Gespräch bluten. */}
          <ThreadContextPanel key={thread.id} thread={thread} demoData={inbox.demoCtx} />
        </ThreadContextHost>
      )}
    </>
  );
}
